import React from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Image as ImageIcon, Lock, Download, Maximize2, FileText, Layout, BarChart, Gavel } from "lucide-react";

const MediaTab: React.FC = () => {
    return (
        <div className="px-4 space-y-8 mt-6 pb-24">
            {/* Photo Gallery */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold flex items-center gap-2">
                        <ImageIcon className="text-primary w-5 h-5" />
                        Project Gallery
                    </h2>
                    <Badge variant="secondary" className="text-[10px] px-2 py-0.5 rounded">12 Items</Badge>
                </div>

                <div className="columns-2 gap-3 space-y-3">
                    {[
                        { h: "180px", color: "bg-indigo-100", label: "Main View" },
                        { h: "120px", color: "bg-slate-200", label: "Hardware" },
                        { h: "160px", color: "bg-emerald-100", label: "Team" },
                        { h: "140px", color: "bg-blue-100", label: "Facility" },
                        { h: "100px", color: "bg-orange-100", label: "Location" }
                    ].map((item, idx) => (
                        <div
                            key={idx}
                            className={`break-inside-avoid relative group rounded-xl overflow-hidden shadow-sm ${item.color}`}
                            style={{ height: item.h }}
                        >
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Maximize2 className="text-white w-6 h-6" />
                            </div>
                            {idx % 2 === 1 && (
                                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur rounded text-[10px] text-white font-bold">
                                    {item.label}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <Button variant="secondary" className="w-full mt-4 py-3 text-sm font-semibold text-primary bg-primary/5 hover:bg-primary/10 rounded-xl transition-colors">
                    View All Photos
                </Button>
            </div>

            {/* Data Room */}
            <div className="pb-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold flex items-center gap-2">
                        <Lock className="text-emerald-500 w-5 h-5" />
                        Data Room
                    </h2>
                    <Badge className="text-[10px] uppercase tracking-wider font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 border-none">Secure Access</Badge>
                </div>

                <Card className="rounded-2xl border border-border overflow-hidden shadow-sm">
                    {[
                        { icon: FileText, color: "text-red-500", bg: "bg-red-100", title: "Business Plan 2024", info: "PDF • 2.4 MB • Updated yesterday" },
                        { icon: Layout, color: "text-blue-500", bg: "bg-blue-100", title: "Technical Specifications", info: "DWG • 14.2 MB • Grid Layout v3" },
                        { icon: BarChart, color: "text-green-600", bg: "bg-green-100", title: "Financial Audit Q3", info: "XLSX • 850 KB • Verified" },
                        { icon: Gavel, color: "text-purple-500", bg: "bg-purple-100", title: "Legal Due Diligence", info: "PDF • 5.1 MB • Restricted" }
                    ].map((doc, idx) => (
                        <div
                            key={idx}
                            className={`group flex items-center p-4 border-b border-border hover:bg-accent/50 transition-colors cursor-pointer ${idx === 3 ? 'border-b-0' : ''}`}
                        >
                            <div className={`h-10 w-10 rounded-lg ${doc.bg} dark:bg-opacity-20 flex items-center justify-center ${doc.color} mr-4 shrink-0`}>
                                <doc.icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold truncate">{doc.title}</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">{doc.info}</p>
                            </div>
                            <Button size="sm" className="hidden sm:flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                <Download className="w-3 h-3" /> Get
                            </Button>
                            <Download className="w-4 h-4 text-muted-foreground sm:hidden" />
                        </div>
                    ))}
                </Card>
            </div>
        </div>
    );
};

export default MediaTab;
